import { translateBackend } from '@assets/i18n/config';
import type { Account, AccountTypes } from '@interfaces/account';
import { AccountStates, getAccountTypes } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type { User } from '@interfaces/user';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { List, ListItem, MenuItem, type TableCellProps } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { Dialog } from '@ovComponents/2-component/dialog/dialog';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import {
  TableCell as OvTableCell,
  TableHeadCell as OvTableHeadCell,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { DeprecatedBadge } from '@ovComponents/3-pattern/deprecatedBadge/deprecatedBadge';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { UserContext } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import { isEmpty } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AccountDialogContent } from './types/account.dialogContent';
import { AccountReviewItem } from './types/account.reviewItem';
import { CorporateCashDialogContent } from './types/corporateCash/corporateCash.dialogContent';
import { CorporateCashReviewItem } from './types/corporateCash/corporateCash.reviewItem';
import { FHSADialogContent } from './types/fhsa/fhsa.dialogContent';
import { FHSAReviewItem } from './types/fhsa/fhsa.reviewItem';
import { JointCashDialogContent } from './types/jointCash/jointCash.dialogContent';
import { JointCashReviewItem } from './types/jointCash/jointCash.reviewItem';
import { JointRespFamilyDialogContent } from './types/jointRespFamily/jointRespFamily.dialogContent';
import { JointRespFamilyReviewItem } from './types/jointRespFamily/jointRespFamily.reviewItem';
import { JointRespSingleDialogContent } from './types/jointRespSingle/jointRespSingle.dialogContent';
import { JointRespSingleReviewItem } from './types/jointRespSingle/jointRespSingle.reviewItem';
import { RespAdultDialogContent } from './types/respAdult/respAdult.dialogContent';
import { RespAdultReviewItem } from './types/respAdult/respAdult.reviewItem';
import { RespFamilyDialogContent } from './types/respFamily/respFamily.dialogContent';
import { RespFamilyReviewItem } from './types/respFamily/respFamily.reviewItem';
import { RespSingleDialogContent } from './types/respSingle/respSingle.dialogContent';
import { RespSingleReviewItem } from './types/respSingle/respSingle.reviewItem';
import { RRSPDialogContent } from './types/rrsp/rrsp.dialogContent';
import { RRSPReviewItem } from './types/rrsp/rrsp.reviewItem';
import { TSFADialogContent } from './types/tfsa/tsfa.dialogContent';
import { TFSAReviewItem } from './types/tfsa/tsfa.reviewItem';
import type { IncompleteAffiliation } from './utils';

const numIncompleteAffiliationsTableCols = 3;
const tableCellSx = { width: `${100 / numIncompleteAffiliationsTableCols}%` };
const TableCell = (props: TableCellProps) => (
  <OvTableCell sx={tableCellSx} {...props}>
    {props.children}
  </OvTableCell>
);
const TableHeadCell = (props: TableCellProps) => (
  <OvTableHeadCell sx={tableCellSx} {...props}>
    {props.children}
  </OvTableHeadCell>
);

export const AccountVisual = ({
  options,
  userData,
  isLoading,
  updateAccounts,
  accounts,
  incompleteAffiliations,
  onCreateAccounts,
  continueFunc,
  updateUser,
}: {
  options: any;
  userData: User;
  isLoading: boolean;
  accounts: Account[];
  incompleteAffiliations: { [accountId: string]: IncompleteAffiliation[] };
  updateAccounts: (accounts: Account[]) => void;
  updateUser: (user: User) => void;
  onCreateAccounts: () => void;
  continueFunc: () => void;
}) => {
  const { t } = useTranslation([
    'accountTypes',
    'affiliationTypes',
    'client',
    'workflowCompletions',
  ]);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedAccountType, setSelectedAccountType] = useState<string>('');
  const { activeOrganization, organizationFeatureFlags } =
    useContext(UserContext);
  const openModal = (value = true) => {
    setOpen(value);
  };

  const initDraftAccount = (accountType: string): Account => ({
    type: accountType as AccountTypes,
    applyForGovFunds: [],
    id: '',
    state: AccountStates.ACTIVE,
    user: userData,
    affiliations: [],
  });

  const handleStartAccountCreation = (accountType: string): void => {
    const AccountCreationContent = getAccountContent(accountType);
    if (AccountCreationContent) {
      openModal(true);
    } else {
      handleAccountCreated(initDraftAccount(accountType));
    }
  };

  const handleAccountCreated = (account: Account): void => {
    setSelectedAccountType('');
    updateAccounts([...accounts, account]);
    setOpen(false);
  };

  const getAccountContent = (value: string): JSX.Element | null => {
    const defaultProps = {
      userData,
      onDialogClose: () => openModal(false),
      onAccountCreated: handleAccountCreated,
      showGoalLinkingSection: options?.linkGoals,
    };

    switch (value) {
      case 'CORPORATE_CASH': {
        return <CorporateCashDialogContent {...defaultProps} />;
      }
      case 'TFSA': {
        return <TSFADialogContent {...defaultProps} />;
      }
      case 'FHSA': {
        return <FHSADialogContent {...defaultProps} />;
      }
      case 'RRSP': {
        return <RRSPDialogContent {...defaultProps} />;
      }
      case 'RESP_SINGLE': {
        return <RespSingleDialogContent {...defaultProps} />;
      }
      case 'RESP_SINGLE_JOINT': {
        return <JointRespSingleDialogContent {...defaultProps} />;
      }
      case 'RESP_FAMILY_JOINT': {
        return <JointRespFamilyDialogContent {...defaultProps} />;
      }
      case 'CASH_JOINT': {
        return <JointCashDialogContent {...defaultProps} />;
      }
      case 'RESP_FAMILY': {
        return <RespFamilyDialogContent {...defaultProps} />;
      }
      case 'RESP_ADULT': {
        return (
          <RespAdultDialogContent
            {...{
              ...defaultProps,
              onAccountCreated: (account) => {
                handleAccountCreated(account);
                updateUser(account.user);
              },
            }}
          />
        );
      }
      default: {
        // Logic to determine if the minimal conditions are met to warrant
        // displaying the account creation form
        const bypassAccountCreationForm = !defaultProps.showGoalLinkingSection;
        if (bypassAccountCreationForm) return null;
        return (
          <AccountDialogContent
            {...{
              ...defaultProps,
              accountType: selectedAccountType as AccountTypes,
            }}
          />
        );
      }
    }
  };
  const deleteAccount = (index: number): void => {
    // slice not working for useState array
    const tempAccount: Account[] = [];
    accounts.forEach((item: Account, index2: number) => {
      if (index2 !== index) tempAccount.push(item);
    });
    updateAccounts([...tempAccount]);
  };
  const getUpdateAffiliationsFn =
    (indexAccount: number) => (affiliations: Affiliation[]) => {
      const updatedAccounts = accounts.map((item: Account, index: number) =>
        index === indexAccount ? { ...item, affiliations } : item,
      );
      updateAccounts(updatedAccounts);
    };
  const getAccountReviewItem = (
    account: Account,
    index: number,
  ): JSX.Element => {
    const defaultProps = {
      account,
      key: index,
      onDeleteAccount: () => deleteAccount(index),
    };
    switch (account.type) {
      case 'CORPORATE_CASH': {
        return (
          <CorporateCashReviewItem
            {...{
              ...defaultProps,
              updateAffiliations: getUpdateAffiliationsFn(index),
            }}
          />
        );
      }
      case 'TFSA': {
        return <TFSAReviewItem {...defaultProps} />;
      }
      case 'FHSA': {
        return <FHSAReviewItem {...defaultProps} />;
      }
      case 'RESP_SINGLE': {
        return <RespSingleReviewItem {...defaultProps} />;
      }
      case 'RRSP': {
        return <RRSPReviewItem {...defaultProps} />;
      }
      case 'RESP_FAMILY': {
        return <RespFamilyReviewItem {...defaultProps} />;
      }
      case 'RESP_ADULT': {
        return <RespAdultReviewItem {...defaultProps} />;
      }
      case 'RESP_SINGLE_JOINT': {
        return <JointRespSingleReviewItem {...defaultProps} />;
      }
      case 'RESP_FAMILY_JOINT': {
        return <JointRespFamilyReviewItem {...defaultProps} />;
      }
      case 'CASH_JOINT': {
        return <JointCashReviewItem {...defaultProps} />;
      }
      default: {
        return <AccountReviewItem {...defaultProps} />;
      }
    }
  };

  const getIncompleteAffiliationRow = (
    entry: IncompleteAffiliation,
    index: number,
  ) => (
    <TableRow key={index}>
      <TableCell>
        {generateClientNameString(entry?.affiliation?.user)}
      </TableCell>
      <TableCell>{t(`affiliationTypes:${entry?.affiliation?.type}`)}</TableCell>
      <TableCell>
        <List>
          {entry.incompleteFields.map((field, index2) => (
            <ListItem key={index2}>{t(`client:details.${field}`)}</ListItem>
          ))}
        </List>
      </TableCell>
    </TableRow>
  );

  const getAccountSection = (entries: IncompleteAffiliation[]): JSX.Element => {
    const { account } = entries[0];

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {t(`accountTypes:${account.type}`)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeadCell>
                  {t(
                    'workflowCompletions:baseAccountCreation.affiliationTable.nameHeader',
                  )}
                </TableHeadCell>
                <TableHeadCell>
                  {t(
                    'workflowCompletions:baseAccountCreation.affiliationTable.typeHeader',
                  )}
                </TableHeadCell>
                <TableHeadCell>
                  {t(
                    'workflowCompletions:baseAccountCreation.affiliationTable.requiredInfoHeader',
                  )}
                </TableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) =>
                getIncompleteAffiliationRow(entry, index),
              )}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>
    );
  };

  const getConfirmationModal = (): JSX.Element => {
    const sections = (
      <Grid
        container
        sx={{
          gap: 1,
        }}
      >
        <Grid size={12}>
          <Typography variant='bodyLarge'>
            {t(
              'workflowCompletions:baseAccountCreation.incompleteAffiliationWarning.bodyText',
            )}
          </Typography>
        </Grid>
        {Object.values(incompleteAffiliations).map((accountEntries) => (
          <Grid size={12}>{getAccountSection(accountEntries)}</Grid>
        ))}
      </Grid>
    );

    return (
      <ConfirmationModal
        open={true}
        onConfirm={continueFunc}
        onCancel={continueFunc}
        title={t(
          'workflowCompletions:baseAccountCreation.incompleteAffiliationWarning.title',
        )}
        children={sections}
        confirmButtonLabel={'Continue'}
        confirmButtonColor='primary'
        showCancelButton={false}
      />
    );
  };

  // new accounts do not have an ID yet
  const accountsToBeCreated = accounts.filter(
    (account: Account) => !account.id,
  );

  return (
    <>
      {!isEmpty(incompleteAffiliations) && getConfirmationModal()}
      <WorkflowCompletionForm
        showSubmitButton={
          accountsToBeCreated.filter((account: Account) => !account.id).length >
          0
        }
        submitButtonProps={{
          label: t('workflowCompletions:createAccountButtonTitle', {
            number: accountsToBeCreated.length,
          }),
          disabled: isLoading,
        }}
        onSubmit={onCreateAccounts}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant='displayLarge' sx={{ mt: 1 }}>
            {translateBackend(options?.title)}
          </Typography>
          <DeprecatedBadge />
        </Box>
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
        {accounts.map((item: Account, index: number) =>
          getAccountReviewItem(item, index),
        )}
        <Grid
          container
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'end',
            mb: 2,
          }}
        >
          <Grid size={8}>
            <SelectField
              onChange={(e: any) => setSelectedAccountType(e.target.value)}
              label={t('workflowCompletions:accountType')}
              fullWidth
              value={selectedAccountType}
            >
              {getAccountTypes(
                userData.type,
                Array.from(organizationFeatureFlags),
                activeOrganization.applicableLocalization?.countries,
              ).map((accountType) => (
                <MenuItem key={accountType} value={accountType}>
                  {t(`accountTypes:${accountType}`)}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
          <Grid size={4}>
            <Button
              variant='outlined'
              disabled={!selectedAccountType}
              onClick={() => handleStartAccountCreation(selectedAccountType)}
              sx={{ textAlign: 'center' }}
              type='button'
            >
              {t('workflowCompletions:start')}
            </Button>
          </Grid>
        </Grid>
        <Dialog
          sx={{
            '.MuiDialog-container': {
              '.MuiPaper-root': {
                padding: '20px',
              },
            },
          }}
          open={open}
          onClose={() => setOpen(false)}
          maxWidth='sm'
          fullWidth
        >
          {getAccountContent(selectedAccountType)}
        </Dialog>
      </WorkflowCompletionForm>
    </>
  );
};

export default AccountVisual;
